import ActiveToggleButton from "@/components/ActiveToggleButton";
import BaseTable from "@/components/BaseTable/BaseTable";
import CustomButton from "@/components/common/Button";
import { getTenantId } from "@/lib/storeManager";
import { TenantRoleService } from "@/services/APIs";
import { gridName } from "@/utils/constant/gridName";
import { tableName } from "@/utils/constant/tableName";
import navigationRoutes from "@/utils/navigationRoutes";
import { Role } from "@/utils/types/tenantPermissions";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Popconfirm } from "antd";
import { Edit2, Plus, Trash } from "lucide-react";
import { useState } from "react";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { useSearchParams } from "react-router-dom";

const RoleListPage: React.FC = () => {
  const tenantId = getTenantId() ?? "";
  const navigate = useAppNavigate();
  const [searchParams] = useSearchParams();
  const urlPage = parseInt(searchParams.get("page") ?? "1", 10);
  const urlPageSize = parseInt(searchParams.get("pageSize") ?? "10", 10);
  const [page, setPage] = useState(urlPage);
  const [pageSize, setPageSize] = useState(urlPageSize);

  // Use React Query for listing roles
  const {
    data,
    isLoading,
    refetch: refetchRoles,
  } = useQuery({
    queryKey: ["tenantRoles", tenantId, page, pageSize],
    queryFn: () => TenantRoleService.listRoles(page, pageSize),
    enabled: !!tenantId,
  });

  const handleEdit = (id: string) => {
    navigate(navigationRoutes.ROLE_EDIT(id));
  };

  const handleCreate = () => {
    navigate(navigationRoutes.ROLE_ADD);
  };

  const handleDelete = async (id: string) => {
    const response = await TenantRoleService.deleteRole(id);
    if (response) {
      refetchRoles();
    }
  };

  const columns: ProColumns<Role>[] = [
    {
      title: "Role Name",
      dataIndex: "name",
      sorter: (a, b) => a.name!.localeCompare(b.name!),
    },
    {
      title: "Description",
      dataIndex: "description",
      render: text => text ?? "—",
      ellipsis: true,
    },
    {
      title: "Is Active",
      width: 120,
      render: (record: any) => {
        return (
          <ActiveToggleButton
            table={tableName.role}
            pk={record.id}
            is_active={record.is_active}
          />
        );
      },
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          onClick={() => handleEdit(record.id)}
          icon={<Edit2 size={16} />}
          type="text"
          title="Edit Role"
        />,
        <Popconfirm
          key="delete"
          title="Are you sure you want to delete this role?"
          onConfirm={() => handleDelete(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <CustomButton
            icon={<Trash size={16} />}
            type="text"
            danger
            title="Delete Role"
          />
        </Popconfirm>,
      ],
    },
  ];

  const toolBarRender = () => [
    <CustomButton
      key="add"
      type="primary"
      icon={<Plus size={16} />}
      onClick={handleCreate}
      title="Add New Role"
    >
      Add Role
    </CustomButton>,
  ];

  return (
    <div>
      <BaseTable<Role>
        loading={isLoading}
        columns={columns}
        dataSource={data?.results ?? []}
        page={page}
        pageSize={pageSize}
        total={data?.count}
        onPageChange={(page, size) => {
          setPage(page);
          setPageSize(size ?? 10);
          searchParams.set("page", page.toString());
          searchParams.set("pageSize", size?.toString() ?? "10");
          navigate(`${navigationRoutes.ROLES}?${searchParams.toString()}`);
        }}
        rowKey="id"
        toolBarRender={toolBarRender}
        headerTitle="Roles List"
        request={() => refetchRoles()}
        tableName={gridName.roleTable}
      />
    </div>
  );
};

export default RoleListPage;
